import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';

import store from '../../store';
import {conditional} from '../../util/conditional';
import {getProductData} from '../../util/getProductData';
import {hasFeatureValue, hasNoFeatureValues, pimText, pimTextDebug, spcExpliciteChoiceDecision} from '../../util/values';
import {selectSpcConfigurations, selectProductDatas, selectSpcSessions} from '../../selectors';
import {attributeIdToString} from '../spc/util/spcUtils';
import {regionCheck} from '../../util/regionCheck';
import {startSpcSession} from '../../slices/spcSlice';
import {fetchAll} from '../../slices/productDataSlice';

@customElement('model-data')
export class ModelData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}
			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	@property({type: Object})
	model = {};

	@property({type: String})
	region = 'ger';

	@property({type: String})
	mode = 'external';

	constructor() {
		super();
		this.productData = null;
		this.spcAttributes = null;
		this.satisfiedAttributes = [];
		this.unSatisfiedAttributes = [];
		this.instanceId = null;
		this.spcSession = null;
		store.subscribe(() => this.requestUpdate());
	}
	async connectedCallback() {
		super.connectedCallback();
		this.instanceId = 'modelPage_' + this.model.model_index;
		if (this.model.model_id && this.model.model_id !== '') {
			await store.dispatch(startSpcSession({deploymentName: this.model.model_id, instance: this.instanceId}));
			this.spcSession = selectSpcSessions(this.state)[this.instanceId];

			// set preset attributs in SPC configuration for this model
			const presetAttributes = this.model.model_attributes?.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
			this.spcSession.makeManyDecisions(presetAttributes, {type: 'KeepExistingDecisions'});

			store.dispatch(fetchAll({useAsDefault: false, mode: this.mode, modelId: this.model.model_id, region: this.region}));
		}
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;

		const productDatas = selectProductDatas(this.state);

		if (Object.hasOwn(productDatas, this.model.model_id)) {
			this.productData = productDatas[this.model.model_id].productData;
			this.conditionalVariantValues = productDatas[this.model.model_id].conditionalVariantValues;
		}

		const spcConfigurations = selectSpcConfigurations(state);
		if (spcConfigurations && Object.hasOwn(spcConfigurations, this.instanceId)) {
			this.spcConfiguration = spcConfigurations[this.instanceId];
			this.spcAttributes = Array.from(this.spcConfiguration.attributes.values());
		}
		if (this.spcAttributes?.length > 0 && this.productData) {
			//console.log('ModelData productData', this.productData);
			//console.log('ModelData conditionalVariantValues', this.conditionalVariantValues);

			this.spcAttributes = this.spcAttributes
				.filter(spcAttribute => {
					const featureProductData = getProductData(spcAttribute.id.localId, this.productData);
					return this.debugShowAll || featureProductData?.isVisible && regionCheck(featureProductData, this.region);
				}) // filter when configured to not be visible in PIM
				.filter(spcAttribute => spcAttribute.id.localId !== 'CX_Region') // never show Region
				.filter(spcAttribute => spcAttribute.id.localId !== 'LE_LuminousFlux_1') // never show Region
				.sort((a, b) =>
					getProductData(a.id.localId, this.productData).displaySequenceUi -
					getProductData(b.id.localId, this.productData).displaySequenceUi
				);
			// this.satisfiedAttributes = this.spcAttributes.filter(attribute => attribute.isSatisfied);
			// this.unSatisfiedAttributes = this.spcAttributes.filter(attribute => !attribute.isSatisfied);
			// console.log('ModelData attributes', this.instanceId, this.satisfiedAttributes, this.unSatisfiedAttributes);
		}

	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		if (!this.productData || !this.spcAttributes) return null;

		return html`
			<h3 class="subheadline">${msg('Properties')}</h3>

			${when(this.spcAttributes.length > 0, () => html`
				${repeat(this.spcAttributes,
					(attribute) => attributeIdToString(attribute.id),
					(attribute) => html`
						<shared-specsheet-feature>
							<trans-label slot="label" key="${attribute.id.localId}" context="F"></trans-label>
							<spc-values-label instanceId="${this.instanceId}" slot="value" class="value" attributeKey="${attribute.id.localId}"
											  context="FV.S" .replace="${{None: msg('None')}}"></spc-values-label>
						</shared-specsheet-feature>
					`)}
			`)}

			<shared-technical-data-attributes .model="${this.model}"></shared-technical-data-attributes>

        `;
	}
}
